import { createChart, CrosshairMode } from 'lightweight-charts';
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { POLYGON_API_KEY } from "../../config";
import SelectTicker from './components/SelectTicker';
import { MarketDataDelaySubscribeInline } from './components/MarketDataDelaySubscribeHeader';
import useMarketDataSubscription from './hooks/useMarketDataSubscription';
import { HiOutlineChartBar } from "react-icons/hi";

const Graph = ({ selected, setSelected }) => {
  const { theme } = useSelector((state) => state.theme);
  const subscribed = useMarketDataSubscription();
  const containerRef = useRef();
  const hasTicker = Boolean(selected && selected !== "all");

  useEffect(() => {
    if (!hasTicker) return undefined;
    const scriptId = 'tradingview-widget-script';
    let script = document.getElementById(scriptId);
    
    const loadWidget = () => {
      if (window.TradingView && containerRef.current) {
        new window.TradingView.widget({
          container_id: "tradingview_0",
          symbol: selected,
          interval: "D",
          theme: theme === 'dark' ? "dark" : "light",
          style: "1",
          locale: "en",
          toolbar_bg: theme === 'dark' ? "#0f172a" : "#f1f3f6",
          enable_publishing: false,
          allow_symbol_change: true,
          autosize: true,
          width: '100%',
          height: '100%',
          timeScale: {
            timeVisible: true,
            secondsVisible: false,
          },
        });
      }
    };

    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://s3.tradingview.com/tv.js";
      script.async = true;
      script.onload = loadWidget;
      document.head.appendChild(script);
    } else {
      loadWidget();
    }

    return () => {
       // We don't necessarily want to remove the script as other components might use it
       // but we should clear the container if needed.
    };
  }, [selected, theme, hasTicker]);

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className={`flex min-w-0 flex-col gap-3 border-b p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4 ${
        theme === 'dark' ? 'border-slate-800 bg-slate-900/50' : 'border-slate-100 bg-slate-50/50'
      }`}>
        <div className="flex min-w-0 items-center gap-3">
          <div className="shrink-0 rounded-lg bg-blue-500/10 p-2 text-blue-500">
            <HiOutlineChartBar size={22} />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2 sm:gap-y-1">
            <h2 className="text-base font-bold sm:text-lg">Price Chart</h2>
            {!subscribed && <MarketDataDelaySubscribeInline theme={theme} />}
          </div>
        </div>
        <SelectTicker selected={selected} setSelected={setSelected} />
      </div>

      {/* Chart Container */}
      <div className="flex-1 p-0 bg-slate-950/20">
        {!hasTicker ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] px-6 text-center">
            <div
              className={`mb-4 flex h-16 w-16 items-center justify-center rounded-full ${
                theme === "dark" ? "bg-slate-800" : "bg-slate-100"
              }`}
            >
              <HiOutlineChartBar className="text-slate-400" size={32} />
            </div>
            <h3 className={`text-lg font-semibold ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>
              Select a ticker to show data
            </h3>
            <p className="mt-2 max-w-sm text-sm text-slate-500">
              Choose a symbol above to load the interactive price chart for that ticker.
            </p>
          </div>
        ) : (
          <div
            className={`relative h-[600px] overflow-hidden border ${
              theme === "dark" ? "border-slate-800" : "border-slate-200"
            }`}
          >
            <div id="tradingview_0" ref={containerRef} className="h-full w-full" />
          </div>
        )}
      </div>
    </div>
  );
};

export default Graph;
